import {
  acceptIncomingPairingRequest,
  acceptTempChatInvite,
  cancelTempChatOutgoing,
  endTempChatEarly,
  formatTempRemaining,
  getDisplayNickname,
  getMyShareCode,
  labelForContact,
  listPendingIncomingTempChats,
  listTempChats,
  pairFromZnOrPayload,
  rejectTempChatInvite,
  setCurrentChatContactId,
} from '../../app/zionApp.ts'
import {
  contactDisplayName,
  findContact,
  getPendingPairingRequest,
  listContacts,
  onContactsChanged,
  removeContactCompletely,
  setPendingPairingRequest,
  contactHandle,
  type ZionContact,
} from '../../storage/pairingStore.ts'
import { loadMessages, onMessagesChanged, previewLineFromMessage } from '../../storage/chatStore.ts'
import { enrichContactsForChatList } from '../../storage/chatListSort.ts'
import { onPinnedChatsChanged } from '../../storage/pinnedChatsStore.ts'
import { formatUnreadCount, getUnread, onUnreadChanged } from '../../storage/unreadStore.ts'
import { findActiveTempForContact, getTempChatByKey, onTempChatsChanged } from '../../storage/tempChatStore.ts'
import { tempChatUnreadId, TEMP_CHAT_DURATIONS } from '../../tempChat/tempChatUtils.ts'

import { t, tf } from '../../i18n/index.ts'
import { applyAppLayout } from '../layout.ts'
import { appendBlocks, el, escapeHtml } from '../dom.ts'
import { navigate } from '../router.ts'
import { unlockNotificationAudio } from '../../notifications/audioUnlock.ts'
import type { RenderStaleCheck } from '../renderGuard.ts'
import { createGlassChatCard } from '../components/chatList.ts'
import { readZnCodeInput, wireZnCodeInput, znCodeInputHtml } from '../znCodeInput.ts'
import { exitApp } from '../../app/exitApp.ts'
import { getSessionToken } from '../../storage/sessionStore.ts'
import {
  beginNotificationPermissionRequest,
  enableWebPush,
  pushActivationBannerMode,
  shouldShowPushActivationBanner,
} from '../../services/pushService.ts'

async function setupHomePushBanner(root: HTMLElement): Promise<void> {
  if (root.querySelector('#home-push-banner')) return
  if (!(await shouldShowPushActivationBanner())) return

  const mode = pushActivationBannerMode()
  if (mode === 'hidden') return

  const anchor = root.querySelector('#zn-pair-card')
  const isInstall = mode === 'ios_install'
  const banner = el(`
    <section class="card highlight push-banner" id="home-push-banner">
      <h2 class="card-title">${escapeHtml(t('home_push_banner_title'))}</h2>
      <p class="status muted small" id="home-push-banner-status">
        ${escapeHtml(isInstall ? t('home_push_banner_ios') : t('home_push_banner_body'))}
      </p>
      <button type="button" id="${isInstall ? 'btn-home-add-to-home' : 'btn-home-enable-push'}">
        ${escapeHtml(isInstall ? t('home_push_banner_add_home') : t('home_push_banner_btn'))}
      </button>
    </section>
  `)
  anchor?.insertAdjacentElement('afterend', banner)

  if (isInstall) {
    banner.querySelector('#btn-home-add-to-home')?.addEventListener('click', () => {
      navigate('addToHome')
    })
    return
  }

  banner.querySelector('#btn-home-enable-push')?.addEventListener('click', () => {
    const statusEl = banner.querySelector('#home-push-banner-status') as HTMLElement
    const btn = banner.querySelector('#btn-home-enable-push') as HTMLButtonElement
    const permissionPromise = beginNotificationPermissionRequest()
    btn.disabled = true
    if (statusEl) statusEl.textContent = t('settings_push_requesting')
    void getSessionToken().then(async (token) => {
      if (!token) {
        if (statusEl) {
          statusEl.textContent = t('settings_push_no_session')
          statusEl.className = 'status err small'
        }
        btn.disabled = false
        return
      }
      const result = await enableWebPush(token, { permissionPromise })
      if (result.ok) {
        banner.remove()
        return
      }
      if (statusEl) {
        statusEl.textContent = result.reason
        statusEl.className = 'status err small'
      }
      btn.disabled = false
    })
  })
}

function formatChatListTime(ts: number): string {
  const d = new Date(ts)
  const dd = String(d.getDate()).padStart(2, '0')
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const hh = String(d.getHours()).padStart(2, '0')
  const mi = String(d.getMinutes()).padStart(2, '0')
  return `${dd}.${mm} ${hh}:${mi}`
}

function previewFromMessage(msg: {
  text?: string
  mediaKind?: string
  mediaFileName?: string
} | null | undefined): string {
  if (!msg) return ''
  return previewLineFromMessage(msg)
}

async function lastMessagePreview(contactId: string, sessionToken?: string): Promise<string> {
  let best: { timestamp: number; text?: string; mediaKind?: string; mediaFileName?: string } | null =
    null
  const primary = await loadMessages(contactId)
  if (primary.length) best = primary[primary.length - 1]!
  const sess = sessionToken?.trim()
  if (sess && sess.toLowerCase() !== contactId.trim().toLowerCase()) {
    const bySess = await loadMessages(sess)
    if (bySess.length) {
      const last = bySess[bySess.length - 1]!
      if (!best || (last.timestamp || 0) >= (best.timestamp || 0)) best = last
    }
  }
  return previewFromMessage(best)
}

function znTagFromShareCode(code: string): string {
  const digits = String(code ?? '')
    .replace(/^ZN-/i, '')
    .replace(/\D/g, '')
  return digits ? `@zn-${digits}` : '@zn-…'
}

let homeContactsUnsub: (() => void) | null = null
let homeUnreadUnsub: (() => void) | null = null
let homeTempUnsub: (() => void) | null = null
let homeMessagesUnsub: (() => void) | null = null
let homePinnedUnsub: (() => void) | null = null
let homeRefreshTimer: number | null = null
let homePaintTimer: number | null = null
let homePaintSerial = 0
let tempInvitePaintSerial = 0

function dedupeContacts(contacts: ZionContact[]): ZionContact[] {
  const bySession = new Map<string, ZionContact>()
  const byNick = new Map<string, ZionContact>()
  for (const c of contacts) {
    const sessionKey = c.sessionToken.trim().toLowerCase()
    const nickKey = c.nickname.trim().toLowerCase()
    const idKey = c.contactId.trim().toLowerCase()
    const key = sessionKey || idKey
    const prev = bySession.get(key)
    if (!prev || c.pairedAt >= prev.pairedAt) bySession.set(key, c)
    if (nickKey) {
      const prevNick = byNick.get(nickKey)
      const chosen = bySession.get(key)!
      if (!prevNick || chosen.pairedAt >= prevNick.pairedAt) byNick.set(nickKey, chosen)
    }
  }
  const unique = new Map<string, ZionContact>()
  for (const c of bySession.values()) unique.set(c.contactId, c)
  for (const c of byNick.values()) unique.set(c.contactId, c)
  return [...unique.values()]
}

async function contactsForHomeList(): Promise<ZionContact[]> {
  await pruneStaleEmptyContacts()
  return dedupeContacts(await listContacts())
}

async function pruneStaleEmptyContacts(): Promise<void> {
  const pending = await getPendingPairingRequest()
  const pendingTok = pending?.fromSessionToken.trim().toLowerCase() ?? ''
  const contacts = await listContacts()
  for (const c of contacts) {
    // Accoppiamento permanente: mai rimuovere contatti con chiave E2E salvata.
    if (c.publicKey?.trim()) continue
    if (c.inboundQueueId) continue
    if (pendingTok && c.sessionToken.trim().toLowerCase() === pendingTok) continue
    const msgs =
      (await loadMessages(c.contactId)).length +
      (c.sessionToken ? (await loadMessages(c.sessionToken)).length : 0)
    if (msgs === 0) await removeContactCompletely(c.contactId)
  }
}

async function paintTempChatList(root: HTMLElement): Promise<void> {
  const listEl = root.querySelector('#temp-chat-list') as HTMLElement | null
  const section = root.querySelector('#home-section-segrete') as HTMLElement | null
  if (!listEl) return
  const temps = listTempChats().filter((t) => t.status !== 'pending_incoming')
  listEl.innerHTML = ''
  if (!temps.length) {
    if (section) section.hidden = true
    return
  }
  if (section) section.hidden = false

  for (const temp of temps) {
    const contact = await findContact(temp.contactId)
    if (!contact) continue
    const remaining = temp.expiresAt - Date.now()
    if (remaining <= 0) continue
    const unread = getUnread(tempChatUnreadId(temp.chatKey))
    const badge =
      unread > 0
        ? `<span class="unread-badge" aria-label="${escapeHtml(tf('home_unread_aria', { count: unread }))}">${escapeHtml(formatUnreadCount(unread))}</span>`
        : ''
    const waiting = temp.status === 'pending_outgoing'
    const canCancel = temp.status === 'pending_outgoing' || temp.status === 'active'
    const subtitle = waiting
      ? t('home_temp_waiting')
      : tf('home_temp_expires', { remaining: formatTempRemaining(remaining) })
    const cancelBtn = canCancel
      ? `<button type="button" class="secondary temp-cancel-btn" data-chat-key="${escapeHtml(temp.chatKey)}" data-cancel-mode="${waiting ? 'pending' : 'active'}">${escapeHtml(t('home_temp_cancel'))}</button>`
      : ''
    const initial = contactDisplayName(contact).slice(0, 1).toUpperCase() || '?'
    const row = el(`
      <div class="list-item has-temp-chat${waiting ? ' temp-pending temp-pending-locked' : ''}">
        ${waiting
          ? `<div class="list-item-main temp-chat-pending" aria-disabled="true">
              <span class="avatar">${escapeHtml(initial)}</span>
              <span class="list-text">
                <strong>${escapeHtml(contactHandle(contact))}</strong>
                <small class="temp-chat-subtitle">${escapeHtml(subtitle)}</small>
              </span>
            </div>`
          : `<button type="button" class="list-item-main temp-chat-open" data-chat-key="${escapeHtml(temp.chatKey)}">
              <span class="avatar">${escapeHtml(initial)}</span>
              <span class="list-text">
                <strong>${escapeHtml(contactHandle(contact))}</strong>
                <small class="temp-chat-subtitle">${escapeHtml(subtitle)}</small>
              </span>
            </button>`}
        <span class="list-item-actions">
          <span class="icon-btn-clock temp-chat-btn" aria-hidden="true"></span>
          ${cancelBtn}${badge}
        </span>
      </div>
    `)
    if (!waiting) {
      row.querySelector('.temp-chat-open')!.addEventListener('click', () => {
        navigate('tempChat', { chatKey: temp.chatKey })
      })
    }
    row.querySelector('.temp-cancel-btn')?.addEventListener('click', (e) => {
      e.stopPropagation()
      e.preventDefault()
      const btn = e.currentTarget as HTMLButtonElement
      const key = btn.dataset.chatKey ?? temp.chatKey
      const mode = btn.dataset.cancelMode ?? 'pending'
      const done = () => scheduleHomeLists(root)
      if (mode === 'active') void endTempChatEarly(key).then(done)
      else void cancelTempChatOutgoing(key).then(done)
    })
    listEl.append(row)
  }
}

function refreshTempCountdowns(root: HTMLElement): void {
  const listEl = root.querySelector('#temp-chat-list')
  if (!listEl) return
  for (const btn of listEl.querySelectorAll('.temp-chat-open[data-chat-key]')) {
    const key = (btn as HTMLElement).dataset.chatKey ?? ''
    const temp = getTempChatByKey(key)
    if (!temp || temp.status === 'pending_incoming') continue
    const remaining = temp.expiresAt - Date.now()
    if (remaining <= 0) continue
    const small = btn.querySelector('.temp-chat-subtitle')
    if (!small) continue
    if (temp.status === 'pending_outgoing') {
      small.textContent = t('home_temp_waiting')
    } else {
      small.textContent = tf('home_temp_expires', { remaining: formatTempRemaining(remaining) })
    }
  }
}

function tempDurationLabel(ms?: number): string {
  if (!ms) return t('home_temp_fallback_label')
  return TEMP_CHAT_DURATIONS.find((d) => d.ms === ms)?.label ?? formatTempRemaining(ms)
}

function renderPendingTempInvites(root: HTMLElement): void {
  const paintSerial = ++tempInvitePaintSerial
  root.querySelectorAll('#temp-invite-card').forEach((n) => n.remove())
  const invites = listPendingIncomingTempChats()
  if (!invites.length) return

  const anchor =
    root.querySelector('.temp-chat-section') ??
    root.querySelector('#zn-pair-card') ??
    root.querySelector('.az-header')
  for (const invite of invites) {
    void findContact(invite.contactId).then((contact) => {
      if (paintSerial !== tempInvitePaintSerial) return
      if (!contact) return
      const nick = invite.fromNickname || contactDisplayName(contact)
      const card = el(`
        <section class="card highlight temp-invite" id="temp-invite-card" data-chat-key="${escapeHtml(invite.chatKey)}">
          <h2>${escapeHtml(t('home_temp_invite_title'))}</h2>
          <p>${tf('home_temp_invite_body', { nick: `<strong>${escapeHtml(nick)}</strong>`, duration: escapeHtml(tempDurationLabel(invite.durationMs)) })}</p>
          <p class="status muted small">${escapeHtml(t('home_temp_invite_hint'))}</p>
          <p class="temp-invite-status status muted"></p>
          <div class="row-btns">
            <button type="button" class="secondary temp-reject-btn">${escapeHtml(t('home_temp_reject'))}</button>
            <button type="button" class="temp-accept-btn">${escapeHtml(t('home_temp_accept'))}</button>
          </div>
        </section>
      `)
      if (paintSerial !== tempInvitePaintSerial) return
      anchor?.insertAdjacentElement('beforebegin', card)

      const statusEl = card.querySelector('.temp-invite-status') as HTMLElement
      const rejectBtn = card.querySelector('.temp-reject-btn') as HTMLButtonElement
      card.querySelector('.temp-accept-btn')!.addEventListener('click', () => {
        void (async () => {
          const acceptBtn = card.querySelector('.temp-accept-btn') as HTMLButtonElement
          acceptBtn.disabled = true
          rejectBtn.disabled = true
          acceptBtn.textContent = t('home_temp_accepting')
          const result = await acceptTempChatInvite(invite.chatKey)
          if (paintSerial !== tempInvitePaintSerial) return
          if (!result.ok) {
            statusEl.textContent = result.error ?? t('common_error')
            statusEl.className = 'temp-invite-status status err'
            acceptBtn.disabled = false
            rejectBtn.disabled = false
            acceptBtn.textContent = t('home_temp_accept')
            return
          }
          card.remove()
          navigate('tempChat', { chatKey: invite.chatKey })
        })()
      })
      rejectBtn.addEventListener('click', () => {
        if (acceptBtnDisabled(card)) return
        rejectBtn.disabled = true
        void rejectTempChatInvite(invite.chatKey).then(() => {
          if (paintSerial !== tempInvitePaintSerial) return
          card.remove()
          scheduleHomeLists(root)
        })
      })
    })
  }
}

function acceptBtnDisabled(card: HTMLElement): boolean {
  const acceptBtn = card.querySelector('.temp-accept-btn') as HTMLButtonElement | null
  return acceptBtn?.disabled === true
}

async function paintContactList(root: HTMLElement, serial: number): Promise<void> {
  const listEl = root.querySelector('#contact-list') as HTMLElement | null
  if (!listEl) return
  const contacts = await contactsForHomeList()
  if (serial !== homePaintSerial) return
  const seen = new Set<string>()
  const uniqueContacts = contacts.filter((c) => {
    const key =
      c.sessionToken.trim().toLowerCase() ||
      c.nickname.trim().toLowerCase() ||
      c.contactId.trim().toLowerCase()
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
  // Pin in alto + ultimi messaggi piÃ¹ recenti subito sotto
  const sorted = await enrichContactsForChatList(uniqueContacts)
  if (serial !== homePaintSerial) return
  listEl.replaceChildren()
  if (!sorted.length) {
    listEl.innerHTML = `<p class="status muted">${escapeHtml(t('home_contacts_empty'))}</p>`
    return
  }
  for (const c of sorted) {
    const unreadContact = getUnread(c.contactId)
    const unreadSession = c.sessionToken ? getUnread(c.sessionToken) : 0
    const unread = findActiveTempForContact(c.contactId)
      ? 0
      : Math.max(unreadContact, unreadSession)
    const initial = contactDisplayName(c).slice(0, 1).toUpperCase() || '?'
    const ts = c.lastMessageTimestamp || c.pairedAt || 0
    const timeLabel = ts ? formatChatListTime(ts) : ''
    const fromDb = await lastMessagePreview(c.contactId, c.sessionToken)
    const preview =
      fromDb ||
      c.lastMessage?.trim() ||
      c.nickname?.trim() ||
      'Nessun messaggio'
    if (serial !== homePaintSerial) return
    const row = createGlassChatCard({
      contactId: c.contactId,
      name: labelForContact(c),
      subtitle: preview,
      avatarInitial: initial,
      unread,
      hasUnread: unread > 0,
      isPinned: !!c.isPinned,
      timeLabel,
      tempChatLabel: t('home_temp_chat_aria'),
      onOpen: () => {
        void unlockNotificationAudio()
        setCurrentChatContactId(c.contactId)
        navigate('chat', { contactId: c.contactId })
      },
      onTempChat: () => navigate('tempChatDuration', { contactId: c.contactId }),
      onPinChanged: () => scheduleHomeLists(root),
    })
    listEl.append(row)
  }
}

async function paintHomeLists(root: HTMLElement): Promise<void> {
  const serial = ++homePaintSerial
  await Promise.all([paintTempChatList(root), paintContactList(root, serial)])
}

function scheduleHomeLists(root: HTMLElement): void {
  if (homePaintTimer != null) window.clearTimeout(homePaintTimer)
  homePaintTimer = window.setTimeout(() => {
    homePaintTimer = null
    void paintHomeLists(root).then(() => renderPendingTempInvites(root))
  }, 16)
}

function renderHomeShell(root: HTMLElement, nick: string, shareCode: string): void {
  applyAppLayout('page')
  root.classList.remove('screen-chat', 'screen-vault', 'vault-home', 'home-ipad', 'home-glass')
  root.classList.add('screen-dashboard', 'home-android', 'home-clean')
  document.documentElement.classList.remove('vault-ui')
  root.innerHTML = ''
  const znTag = znTagFromShareCode(shareCode)
  appendBlocks(
    root,
    `
      <header class="az-header">
        <div class="az-header-left">
          <h1 class="az-brand">ZION <span class="az-dot" aria-hidden="true"></span></h1>
          <p class="az-sub" id="home-user-line">
            <span id="home-nick">${escapeHtml(nick)}</span>
            <span id="home-zn-tag">${escapeHtml(znTag)}</span>
          </p>
        </div>
        <div class="az-header-actions">
          <button type="button" class="az-icon-btn" id="btn-home-search" title="Cerca" aria-label="Cerca">🔍</button>
          <button type="button" class="az-icon-btn" id="btn-pair" title="${escapeHtml(t('home_btn_pair_title'))}" aria-label="Sicurezza">🛡️</button>
          <button type="button" class="az-icon-btn" id="btn-settings-top" title="${escapeHtml(t('home_btn_settings_title'))}" aria-label="Impostazioni">⚙️</button>
        </div>
      </header>

      <section class="az-pair-pill" id="zn-pair-card">
        ${znCodeInputHtml('zn-code')}
        <button type="button" class="az-pair-go" id="btn-zn-pair" title="${escapeHtml(t('home_btn_zn_pair'))}" aria-label="${escapeHtml(t('home_btn_zn_pair'))}">›</button>
        <p id="zn-status" class="status muted"></p>
      </section>

      <section id="home-section-segrete" class="temp-chat-section">
        <h2 class="az-section">// TEMP</h2>
        <div id="temp-chat-list"></div>
      </section>

      <section id="home-section-chat">
        <h2 class="az-section">// CHAT</h2>
        <div id="contact-list"><p class="status muted">${escapeHtml(t('home_loading'))}</p></div>
      </section>

      <button type="button" class="az-fab" id="btn-home-fab" title="${escapeHtml(t('home_btn_open_pair'))}" aria-label="Nuovo">+</button>

      <nav class="az-bottom-nav" aria-label="Navigazione">
        <button type="button" class="az-nav-tab is-active" data-home-tab="chat" id="home-tab-chat">
          <span>CHAT</span>
          <span class="az-nav-indicator" aria-hidden="true"></span>
        </button>
        <button type="button" class="az-nav-tab" data-home-tab="exit" id="home-tab-exit">
          <span>EXIT</span>
          <span class="az-nav-indicator" aria-hidden="true"></span>
        </button>
      </nav>
    `,
  )

  const goPair = () => navigate('pair')
  root.querySelector('#btn-pair')!.addEventListener('click', goPair)
  root.querySelector('#btn-home-fab')!.addEventListener('click', goPair)
  root.querySelector('#btn-settings-top')?.addEventListener('click', () => navigate('settings'))
  root.querySelector('#btn-home-search')?.addEventListener('click', () => {
    const znInput = root.querySelector('#zn-code') as HTMLInputElement | null
    znInput?.focus()
    znInput?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  })
  root.querySelector('#btn-zn-pair')!.addEventListener('click', () => {
    void pairFromZnCode(root)
  })
  const znInput = root.querySelector('#zn-code') as HTMLInputElement | null
  if (znInput) {
    wireZnCodeInput(znInput)
    znInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        void pairFromZnCode(root)
      }
    })
  }

  wireHomeBottomNav(root)
}

function wireHomeBottomNav(root: HTMLElement): void {
  const setActive = (tab: string) => {
    root.querySelectorAll('.az-nav-tab').forEach((el) => {
      el.classList.toggle('is-active', (el as HTMLElement).dataset.homeTab === tab)
    })
  }
  root.querySelector('#home-tab-chat')?.addEventListener('click', () => {
    setActive('chat')
    root.querySelector('#home-section-chat')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  })
  root.querySelector('#home-tab-exit')?.addEventListener('click', () => {
    void exitApp()
  })
}

export async function renderHomeScreen(
  root: HTMLElement,
  isStale: RenderStaleCheck = () => false,
): Promise<void> {
  if (homeContactsUnsub) {
    homeContactsUnsub()
    homeContactsUnsub = null
  }
  if (homeUnreadUnsub) {
    homeUnreadUnsub()
    homeUnreadUnsub = null
  }
  if (homeTempUnsub) {
    homeTempUnsub()
    homeTempUnsub = null
  }
  if (homeMessagesUnsub) {
    homeMessagesUnsub()
    homeMessagesUnsub = null
  }
  if (homePinnedUnsub) {
    homePinnedUnsub()
    homePinnedUnsub = null
  }
  if (homeRefreshTimer != null) {
    window.clearInterval(homeRefreshTimer)
    homeRefreshTimer = null
  }
  if (homePaintTimer != null) {
    window.clearTimeout(homePaintTimer)
    homePaintTimer = null
  }
  homePaintSerial++

  applyAppLayout('page')
  renderHomeShell(root, '…', '…')
  void setupHomePushBanner(root)

  const pending = await getPendingPairingRequest()
  if (isStale()) return

  // Contatti subito (IndexedDB) — header nick/ZN in parallelo senza bloccare la lista
  void Promise.all([getDisplayNickname(), getMyShareCode()]).then(([nick, shareCode]) => {
    if (isStale()) return
    const nickEl = root.querySelector('#home-nick')
    const znEl = root.querySelector('#home-zn-tag')
    if (nickEl) nickEl.textContent = nick
    if (znEl) znEl.textContent = znTagFromShareCode(shareCode)
  })

  await paintHomeLists(root)
  if (isStale()) return
  renderPendingTempInvites(root)

  homeContactsUnsub = onContactsChanged(() => scheduleHomeLists(root))
  homeUnreadUnsub = onUnreadChanged(() => scheduleHomeLists(root))
  homeTempUnsub = onTempChatsChanged(() => scheduleHomeLists(root))
  // Nuovo messaggio → aggiorna lastMessageTimestamp e riordina (sotto i pin)
  homeMessagesUnsub = onMessagesChanged(() => scheduleHomeLists(root))
  homePinnedUnsub = onPinnedChatsChanged(() => scheduleHomeLists(root))
  homeRefreshTimer = window.setInterval(() => {
    if (root.querySelector('#temp-chat-list')) refreshTempCountdowns(root)
  }, 1000)

  if (pending) {
    const { showIncomingPairRequestModal } = await import('../modals.ts')
    showIncomingPairRequestModal({
      nickname: pending.nickname || pending.fromSessionToken.slice(0, 8),
      onAccept: async () => {
        const result = await acceptIncomingPairingRequest(pending)
        if (!result.ok) throw new Error(result.error ?? t('home_pair_accept_failed'))
      },
      onReject: async () => {
        await setPendingPairingRequest(null)
        navigate('home')
      },
    })
  }
}

async function pairFromZnCode(root: HTMLElement): Promise<void> {
  const wrap = root.querySelector('#zn-pair-card') as HTMLElement
  const znInput = wrap.querySelector('#zn-code') as HTMLInputElement
  const code = readZnCodeInput(znInput)
  const status = wrap.querySelector('#zn-status') as HTMLElement
  if (!code || code.length !== 9) {
    status.textContent = t('home_zn_digits_err')
    status.className = 'status err'
    return
  }

  const { showSendPairingModal } = await import('../modals.ts')
  showSendPairingModal({
    codeLabel: code,
    onConfirm: async () => {
      status.textContent = t('home_sending')
      status.className = 'status muted'
      const result = await pairFromZnOrPayload(code)
      status.textContent = result.message
      status.className = `status ${result.ok ? 'ok' : 'err'}`
      if (result.ok) setTimeout(() => navigate('home'), 600)
    },
  })
}
